/** Sections gated by the Majee rights matrix (consultation). */
export const SECTIONS = {
  NEWS: 'news',
  TOOLS: 'tools',
  THERAPEUTIC_AREAS: 'therapeuticAreas',
  PRODUCTS: 'products',
  PATHOLOGIES: 'pathologies',
  CENTERS: 'centers',
  CONTACTS: 'contacts',
  CONGRESSES: 'congresses',
  PROJECTS: 'projects',
  COMPETITORS: 'competitors',
  PLANNING: 'planning',
};

const ALL = ['Médical', 'DAP', 'Ventes', 'Marketing', 'Finances', 'RH', 'Office', 'Marché'];
const PLANNING_DIVISIONS = ['Médical', 'Marketing'];
const PROJECT_TASKS_HIDDEN_DIVISIONS = ['Ventes', 'RH'];

/** Consultation rights per section, based on the July 2026 matrix. */
const CONSULT = {
  [SECTIONS.NEWS]: ALL,
  [SECTIONS.TOOLS]: ALL,
  [SECTIONS.THERAPEUTIC_AREAS]: ['Médical', 'DAP', 'Marketing', 'Finances', 'RH'],
  [SECTIONS.PRODUCTS]: ALL,
  [SECTIONS.PATHOLOGIES]: ['Médical'],
  [SECTIONS.CENTERS]: ['Médical'],
  [SECTIONS.CONTACTS]: ['Médical'],
  [SECTIONS.CONGRESSES]: ALL,
  [SECTIONS.PROJECTS]: ['Médical', 'Marketing'],
  [SECTIONS.COMPETITORS]: ALL,
  [SECTIONS.PLANNING]: PLANNING_DIVISIONS,
};

/** Édition Actualités only — all roles in the matrix. */
const EDIT_NEWS = ALL;

const DIVISION_PRIORITY = [
  'Médical',
  'DAP',
  'Ventes',
  'Marketing',
  'Finances',
  'RH',
  'Office',
  'Marché',
];

export function normalizeDivision(division) {
  return division || null;
}

function scoreDivision(normalized) {
  let score = 0;
  Object.values(SECTIONS).forEach((section) => {
    if (CONSULT[section]?.includes(normalized)) score += 1;
  });
  if (EDIT_NEWS.includes(normalized)) score += 1;
  return score;
}

/**
 * Among user divisions, pick the one with the most rights.
 * Tie-break follows the division order from the source matrix.
 */
export function getEffectiveDivision(divisions = []) {
  const normalized = [...new Set(
    (divisions || []).map(normalizeDivision).filter(Boolean),
  )];
  if (!normalized.length) return null;

  let best = normalized[0];
  let bestScore = scoreDivision(best);

  normalized.forEach((d) => {
    const s = scoreDivision(d);
    if (s > bestScore) {
      best = d;
      bestScore = s;
      return;
    }
    if (s === bestScore) {
      const bi = DIVISION_PRIORITY.indexOf(best);
      const di = DIVISION_PRIORITY.indexOf(d);
      const bp = bi === -1 ? 999 : bi;
      const dp = di === -1 ? 999 : di;
      if (dp < bp) best = d;
    }
  });

  return best;
}

export function canAccessPlanning(divisions = []) {
  return (divisions || []).some((division) => PLANNING_DIVISIONS.includes(division));
}

export function canViewProjectTasks(divisions = []) {
  const effectiveDivision = getEffectiveDivision(divisions);
  return !PROJECT_TASKS_HIDDEN_DIVISIONS.includes(effectiveDivision);
}

export function canConsult(effectiveDivision, section, divisions = []) {
  if (!section) return false;
  if (section === SECTIONS.PLANNING) {
    return canAccessPlanning(divisions);
  }
  if (!effectiveDivision) return false;
  return CONSULT[section]?.includes(effectiveDivision) ?? false;
}

export function canEditNews(effectiveDivision) {
  if (!effectiveDivision) return false;
  return EDIT_NEWS.includes(effectiveDivision);
}

export function getRightsForUser(divisions = []) {
  const effectiveDivision = getEffectiveDivision(divisions);
  return {
    effectiveDivision,
    canEditNews: canEditNews(effectiveDivision),
    canConsult: (section) => canConsult(effectiveDivision, section, divisions),
    canAccessPlanning: canAccessPlanning(divisions),
    canViewProjectTasks: canViewProjectTasks(divisions),
  };
}
